import { forwardRef, useCallback, useImperativeHandle, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Radio } from "lucide-react";
import clsx from "clsx";
import { SearchInput } from "components/common/SearchInput";
import { Button } from "components/common/Button";
import { useAutoScrollToRef } from "hooks/useAutoScrollToRef";
import { GroupsApprovalBarV3 } from "../GroupsApprovalBarV3";
import { ElementGroupScrollableDivideList } from "../../ReviewPage/ElementGroupsPanel/ElementGroupScrollableDivideList";
import { ElementGroupDefaultRow } from "../../ReviewPage/ElementGroupsPanel/ElementGroupDefaultRow";
import { ApprovalStatusCheckbox } from "../../ReviewPage/ApprovalStatusCheckbox";
import { EmptyStateMessage } from "../../ReviewPage/EmptyStateMessage";
import { ElementInspectorV3 } from "../ElementInspectorV3";
import { ElementsCreationModeV3 } from "../ElementsCreationModeV3";
import { AddElementButton } from "../ElementsCreationModeV3/AddElementButton";
import { SelectionAreasControl } from "../SelectionAreasControl";
import { MarkerNotesControl } from "../MarkerNotesControl";
import { SelectionAreasMode } from "../SelectionAreasMode";
import { RoomsControl } from "../RoomsControl";
import { RoomsCreationMode } from "../RoomsCreationMode";
import { RoomsList } from "../RoomsList";
import { ZonesList } from "../ZonesList";
import { WallsSummary } from "../WallsSummary";
import { RoomHeader } from "../RoomHeader";
import { RoomInspector } from "../RoomInspector";
import { ZoneHeader } from "../ZoneHeader";
import { ExportImpactChip } from "../ExportImpactChip";
import { ExportImpactCount } from "../ExportImpactCount";
import { AllElementsGroupRow } from "../AllElementsGroupRow";
import { GroupIcon } from "../GroupIcon";
import { GroupVisibilityToggleButton } from "../GroupVisibilityToggleButton";
import { NeedsReviewIcon } from "../NeedsReviewIcon";
import { TabSortControl } from "../TabSortControl";
import { sortTabList } from "../utils/sortTabList";
import { sumElementMultipliers } from "../utils/sumElementMultipliers";
import { ONBOARDING_ANCHORS } from "../Onboarding/onboardingSelectors";
import type {
  FloorPlanElementsTabV3Handlers,
  FloorPlanElementsTabV3Props,
} from "./types";

export const FloorPlanElementsTabV3 = forwardRef<
  FloorPlanElementsTabV3Handlers,
  FloorPlanElementsTabV3Props
>(function FloorPlanElementsTabV3(
  {
    canEdit,
    elementSearch,
    onElementSearchChange,
    sort,
    onSortChange,
    groupIds,
    byGroupId,
    exportElementsByGroupId,
    groupMeta,
    selectedGroupLabel,
    selectedGroupElements,
    selectedElement,
    selectedPlanRecordIndex,
    allPlanElementsApproved,
    isLoadingApprovals,
    isFloorPlanGroupApproved,
    onOpenBulkApproval,
    onToggleFloorPlanGroupApproval,
    onSelectGroup,
    onJumpToSelectedElementGroup,
    onSelectElement,
    form,
    isEditingElement,
    onToggleEditMode,
    onDeleteElement,
    onDeleteGroup,
    onRenameGroup,
    onMarkElementGroupReviewed,
    onToggleSuspicious,
    isCreationMode,
    onOpenCreationMode,
    workMode,
    isSelectionAreasMode,
    selectionAreasState,
    onOpenAreasPanel,
    areasCount,
    areAreasVisible,
    onToggleAreasVisibility,
    notesCount,
    areNotesVisible,
    onToggleNotesVisibility,
    roomsCreation,
    roomInspector,
    onOpenRoomsPanel,
    isLayoutLoading,
    isLayoutError,
    roomElementCounts,
    layoutViewState,
    selectedRoom,
    onSelectRoom,
    onClearRoomSelection,
    onEditRoom,
    roomsMerge,
    selectedZone,
    selectedZoneRoomIds,
    onSelectZone,
    onClearZoneSelection,
    creationModeState,
    isMultiAddModeEnabled,
    onToggleMultiAddMode,
    isTraversing,
    onStartTraversing,
    onStopTraversing,
    onOpenGridReview,
    isGridReviewOpen,
    seenIds,
    isAllElementsMode,
    onSelectAllElements,
    totalElementCount,
    hiddenElementIds,
    onToggleElementsVisibility,
    areAllElementsHidden,
    boqPositions,
    onSaveLvMatch,
    onFlashRipple,
    iconByGroupId,
  }: FloorPlanElementsTabV3Props,
  ref,
) {
  const { t } = useTranslation();

  const isRoomsCreationMode = roomsCreation !== null;

  const { scrollToTarget, containerRef, targetRef } = useAutoScrollToRef<
    HTMLDivElement,
    HTMLLIElement
  >({
    resetDep: `${selectedGroupLabel ?? "all"}-${selectedPlanRecordIndex}-${isCreationMode}-${isSelectionAreasMode}-${isRoomsCreationMode}-${selectedRoom?.id ?? "no-room"}`,
    options: { block: "start" },
  });

  // Summed once per group so sorting and the row list are plain lookups.
  const elementQuantityByGroupId = useMemo(() => {
    const quantities = new Map<string, number>();
    for (const groupId of groupIds) {
      quantities.set(
        groupId,
        sumElementMultipliers({ elements: byGroupId[groupId] ?? [] }),
      );
    }
    return quantities;
  }, [groupIds, byGroupId]);

  const isExecutionMode = workMode === "execution";
  // Execution is read-only for elements; rooms keep the plain canEdit.
  const canEditElements = canEdit && !isExecutionMode;

  // Selection areas are a planning concept, so execution never shows their export impact.
  const exportSourceByGroupId = isExecutionMode
    ? byGroupId
    : exportElementsByGroupId;

  const { exportQuantityByGroupId, totalExportElementCount } = useMemo(() => {
    const quantities = new Map<string, number>();
    let total = 0;
    for (const groupId of groupIds) {
      const quantity = sumElementMultipliers({
        elements: exportSourceByGroupId[groupId] ?? [],
      });
      quantities.set(groupId, quantity);
      total += quantity;
    }
    return {
      exportQuantityByGroupId: quantities,
      totalExportElementCount: total,
    };
  }, [groupIds, exportSourceByGroupId]);

  const sortedGroupIds = useMemo(
    () =>
      sortTabList({
        items: groupIds,
        mode: sort,
        getLabel: (groupId) => groupId,
        getQuantity: (groupId) => elementQuantityByGroupId.get(groupId) ?? 0,
      }),
    [groupIds, sort, elementQuantityByGroupId],
  );

  const groupTypeOptions = useMemo(
    () =>
      groupIds.map((groupId) => ({
        value: groupId,
        label: groupId,
        endAdornment: (
          <GroupIcon
            dataUrl={iconByGroupId.get(groupId)}
            disableHover
            size="sm"
          />
        ),
      })),
    [groupIds, iconByGroupId],
  );

  const allElementIds = useMemo(
    () =>
      groupIds.flatMap((groupId) =>
        (byGroupId[groupId] ?? []).map((element) => element.id),
      ),
    [groupIds, byGroupId],
  );

  const normalizedElementSearch = elementSearch.trim().toLowerCase();

  useImperativeHandle(
    ref,
    () => ({ scrollToSelectedSection: scrollToTarget }),
    [scrollToTarget],
  );

  const editSelectedRoom = useCallback(() => {
    if (selectedRoom === null) return;
    onStopTraversing();
    onEditRoom(selectedRoom.id);
  }, [selectedRoom, onStopTraversing, onEditRoom]);

  const onEditSelectedRoom =
    canEdit && !isEditingElement ? editSelectedRoom : undefined;

  if (isSelectionAreasMode) {
    return <SelectionAreasMode state={selectionAreasState} />;
  }

  if (roomsCreation !== null) {
    return <RoomsCreationMode state={roomsCreation} />;
  }

  if (isCreationMode) {
    return (
      <div className="h-full flex flex-col">
        <ElementsCreationModeV3
          creationModeState={creationModeState}
          isMultiAddModeEnabled={isMultiAddModeEnabled}
          onToggleMultiAddMode={onToggleMultiAddMode}
          isEditingElement={isEditingElement}
          onToggleEditMode={onToggleEditMode}
        />
      </div>
    );
  }

  if (isExecutionMode) {
    if (isLayoutLoading) return null;
    // Walls need no rooms, so their summary comes before the rooms empty state.
    if (selectedRoom === null && layoutViewState.view === "walls") {
      return <WallsSummary counts={layoutViewState.wallCounts} />;
    }
    // A failed refetch keeps the last rooms, which are still worth showing
    if (isLayoutError && roomElementCounts.length === 0) {
      return <EmptyStateMessage message={t("smartSurveyor.rooms.loadError")} />;
    }
    if (roomElementCounts.length === 0) {
      return (
        <EmptyStateMessage
          message={t(
            canEdit
              ? "smartSurveyor.rooms.emptyState"
              : "smartSurveyor.rooms.emptyStateReadOnly",
          )}
          action={
            <RoomsControl
              count={roomElementCounts.length}
              canEdit={canEdit}
              onOpenPanel={onOpenRoomsPanel}
            />
          }
        />
      );
    }
    if (selectedRoom === null) {
      if (layoutViewState.view === "zones") {
        if (selectedZone !== null) {
          return (
            <>
              <ZoneHeader
                zoneName={selectedZone.name}
                roomCount={selectedZoneRoomIds.size}
                onBack={onClearZoneSelection}
              />
              <RoomsList
                roomElementCounts={roomElementCounts.filter(({ room }) =>
                  selectedZoneRoomIds.has(room.id),
                )}
                roomColorById={layoutViewState.roomColorById}
                canEdit={canEdit}
                onSelectRoom={onSelectRoom}
                onEditRoom={onEditRoom}
                roomInspector={roomInspector}
                roomsMerge={roomsMerge}
              />
            </>
          );
        }
        return (
          <ZonesList
            zones={layoutViewState.zones}
            zoneColorById={layoutViewState.zoneColorById}
            roomCountByZoneId={layoutViewState.roomCountByZoneId}
            onSelectZone={onSelectZone}
          />
        );
      }
      return (
        <RoomsList
          roomElementCounts={roomElementCounts}
          roomColorById={layoutViewState.roomColorById}
          canEdit={canEdit}
          onOpenRoomsPanel={onOpenRoomsPanel}
          onSelectRoom={onSelectRoom}
          onEditRoom={onEditRoom}
          roomInspector={roomInspector}
          roomsMerge={roomsMerge}
        />
      );
    }
    // Editing from inside keeps the room entered; its panel replaces the elements until Save or Cancel.
    if (roomInspector !== null) {
      return (
        <>
          <RoomHeader
            roomName={selectedRoom.name}
            elementCount={totalElementCount}
            // Back drops the unsaved edit, the same as Cancel, and then leaves the room.
            onBack={() => {
              roomInspector.cancel();
              onClearRoomSelection();
            }}
          />
          <RoomInspector state={roomInspector} />
        </>
      );
    }
    if (groupIds.length === 0) {
      return (
        <>
          <RoomHeader
            roomName={selectedRoom.name}
            elementCount={0}
            onBack={onClearRoomSelection}
            onEdit={onEditSelectedRoom}
          />
          <EmptyStateMessage
            message={t("smartSurveyor.reviewPage.noFloorPlanElements")}
          />
        </>
      );
    }
  } else if (groupIds.length === 0) {
    return (
      <EmptyStateMessage
        message={t("smartSurveyor.reviewPage.noFloorPlanElements")}
        action={
          canEdit ? (
            <AddElementButton onClick={onOpenCreationMode} />
          ) : undefined
        }
      />
    );
  }

  return (
    <>
      {isExecutionMode && selectedRoom !== null && (
        <RoomHeader
          roomName={selectedRoom.name}
          elementCount={totalElementCount}
          onBack={onClearRoomSelection}
          onEdit={onEditSelectedRoom}
        />
      )}
      <div
        data-onboarding={ONBOARDING_ANCHORS.searchInput}
        className="flex items-center gap-2 mb-2"
      >
        <SearchInput
          value={elementSearch}
          onChange={onElementSearchChange}
          placeholder={t("smartSurveyor.reviewPage.searchPlaceholder")}
          className="flex-1"
        />
        <TabSortControl value={sort} onChange={onSortChange} />
      </div>
      <div data-onboarding={ONBOARDING_ANCHORS.approvalBar}>
        <GroupsApprovalBarV3
          summaryText={`${t("smartSurveyor.reviewPage.groupsCount", { count: groupIds.length })} • ${t("smartSurveyor.reviewPage.elementsCount", { count: totalElementCount })}`}
          summaryAdornment={
            totalExportElementCount !== totalElementCount && (
              <ExportImpactChip
                label={String(totalExportElementCount)}
                tooltip={t("smartSurveyor.selectionAreas.exportImpactTooltip", {
                  exportValue: totalExportElementCount,
                  totalValue: totalElementCount,
                })}
              />
            )
          }
          allApproved={allPlanElementsApproved}
          onToggleApproval={onOpenBulkApproval}
          isLoading={isLoadingApprovals}
          canEdit={canEditElements}
          startAdornment={
            <>
              {onFlashRipple && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onFlashRipple}
                  startAdornment={<Radio className="w-3.5 h-3.5" />}
                  className="shrink-0"
                >
                  {t("smartSurveyor.reviewPage.locateMarkers")}
                </Button>
              )}
              {!isExecutionMode && (
                <SelectionAreasControl
                  count={areasCount}
                  canEdit={canEdit}
                  areAreasVisible={areAreasVisible}
                  onOpenPanel={onOpenAreasPanel}
                  onToggleVisibility={onToggleAreasVisibility}
                />
              )}
              {!isExecutionMode && (
                <MarkerNotesControl
                  count={notesCount}
                  areNotesVisible={areNotesVisible}
                  onToggleVisibility={onToggleNotesVisibility}
                />
              )}
              {!isExecutionMode && canEdit && (
                <AddElementButton onClick={onOpenCreationMode} />
              )}
            </>
          }
        />
      </div>
      <ul
        data-onboarding={ONBOARDING_ANCHORS.categoryList}
        className="overflow-hidden flex flex-col min-h-0"
      >
        <ElementGroupScrollableDivideList
          containerRef={containerRef}
          className="p-1"
        >
          <AllElementsGroupRow
            canEdit={canEditElements}
            totalElementCount={
              totalExportElementCount !== totalElementCount ? (
                <ExportImpactCount
                  totalLabel={String(totalElementCount)}
                  exportLabel={String(totalExportElementCount)}
                  tooltip={t(
                    "smartSurveyor.selectionAreas.exportImpactTooltip",
                    {
                      exportValue: totalExportElementCount,
                      totalValue: totalElementCount,
                    },
                  )}
                />
              ) : (
                totalElementCount
              )
            }
            isAllElementsMode={isAllElementsMode}
            onSelectAllElements={onSelectAllElements}
            targetRef={targetRef}
            selectedGroupLabel={selectedGroupLabel}
            selectedGroupElements={selectedGroupElements}
            selectedElement={selectedElement}
            groupMeta={groupMeta}
            groupTypeOptions={groupTypeOptions}
            form={form}
            onSelectElement={onSelectElement}
            onJumpToSelectedElementGroup={onJumpToSelectedElementGroup}
            onRenameGroup={onRenameGroup}
            isEditingElement={isEditingElement}
            onToggleEditMode={onToggleEditMode}
            onDeleteElement={onDeleteElement}
            isTraversing={isTraversing}
            onStartTraversing={onStartTraversing}
            onStopTraversing={onStopTraversing}
            onOpenGridReview={onOpenGridReview}
            isGridReviewOpen={isGridReviewOpen}
            seenIds={seenIds}
            allElementIds={allElementIds}
            hiddenElementIds={hiddenElementIds}
            onToggleElementsVisibility={onToggleElementsVisibility}
            areAllElementsHidden={areAllElementsHidden}
            boqPositions={boqPositions}
            onSaveLvMatch={onSaveLvMatch}
            iconByGroupId={iconByGroupId}
          />
          {sortedGroupIds
            .filter(
              (groupId) =>
                groupId.toLowerCase().includes(normalizedElementSearch) ||
                groupId === selectedGroupLabel,
            )
            .map((groupId, index) => {
              const elements = byGroupId[groupId] ?? [];
              const isGroupSelected = selectedGroupLabel === groupId;
              const meta = groupMeta?.[groupId];

              if (!meta) return null;

              const elementQuantity =
                elementQuantityByGroupId.get(groupId) ?? 0;
              const exportQuantity = exportQuantityByGroupId.get(groupId) ?? 0;

              const isGroupHidden = elements.every((element) =>
                hiddenElementIds.has(element.id),
              );
              const isGroupApproved = isFloorPlanGroupApproved(
                meta.element_type_id,
              );
              const isSuspiciousUnapproved =
                !isGroupApproved && meta.nameStatus === "suspicious";

              return (
                <ElementGroupDefaultRow
                  key={groupId}
                  index={index}
                  title={groupId}
                  highlightQuery={normalizedElementSearch}
                  count={
                    exportQuantity !== elementQuantity ? (
                      <ExportImpactCount
                        totalLabel={String(elementQuantity)}
                        exportLabel={String(exportQuantity)}
                        tooltip={t(
                          "smartSurveyor.selectionAreas.exportImpactTooltip",
                          {
                            exportValue: exportQuantity,
                            totalValue: elementQuantity,
                          },
                        )}
                      />
                    ) : (
                      elementQuantity
                    )
                  }
                  isSelected={isGroupSelected}
                  onClick={() => !isGroupSelected && onSelectGroup(groupId)}
                  rowRef={isGroupSelected ? targetRef : undefined}
                  dataOnboarding={
                    index === 0
                      ? ONBOARDING_ANCHORS.categoryRowFirst
                      : undefined
                  }
                  className={clsx({
                    "bg-green-50 hover:bg-green-100": isGroupApproved,
                    "bg-amber-50 hover:bg-amber-100": isSuspiciousUnapproved,
                  })}
                  containerClassName={
                    isGroupSelected
                      ? "!border-2 !border-blue-400 rounded-lg overflow-hidden my-1"
                      : undefined
                  }
                  startAdornment={
                    <>
                      <GroupVisibilityToggleButton
                        isHidden={isGroupHidden}
                        onToggle={() =>
                          onToggleElementsVisibility(
                            elements.map((element) => element.id),
                          )
                        }
                        title={
                          isGroupHidden
                            ? t("smartSurveyor.reviewPage.showGroup")
                            : t("smartSurveyor.reviewPage.hideGroup")
                        }
                      />
                      <GroupIcon
                        dataUrl={iconByGroupId.get(groupId)}
                        disableHover={isGroupSelected}
                      />
                    </>
                  }
                  endAdornment={
                    <div className="flex items-center gap-2">
                      {isSuspiciousUnapproved && !isGroupSelected && (
                        <NeedsReviewIcon />
                      )}
                      {!isExecutionMode && (
                        <ApprovalStatusCheckbox
                          checked={isGroupApproved}
                          onToggle={() =>
                            onToggleFloorPlanGroupApproval(meta.element_type_id)
                          }
                        />
                      )}
                    </div>
                  }
                >
                  {isGroupSelected && (
                    <ElementInspectorV3
                      canEdit={canEditElements}
                      form={form}
                      selectedGroupId={groupId}
                      groupElements={selectedGroupElements}
                      selectedElement={selectedElement}
                      groupLvMatch={meta.lvMatch}
                      boqPositions={boqPositions}
                      onSaveLvMatch={onSaveLvMatch}
                      groupTypeOptions={groupTypeOptions}
                      onSelectElement={onSelectElement}
                      onRenameGroup={onRenameGroup}
                      nameStatus={meta.nameStatus}
                      onMarkReviewed={() => onMarkElementGroupReviewed(groupId)}
                      onToggleSuspicious={onToggleSuspicious}
                      isEditingElement={isEditingElement}
                      onToggleEditMode={onToggleEditMode}
                      onDeleteElement={onDeleteElement}
                      onDeleteGroup={onDeleteGroup}
                      isTraversing={isTraversing}
                      onStartTraversing={onStartTraversing}
                      onStopTraversing={onStopTraversing}
                      onOpenGridReview={onOpenGridReview}
                      isGridReviewOpen={isGridReviewOpen}
                      seenIds={seenIds}
                      hiddenElementIds={hiddenElementIds}
                      onToggleElementsVisibility={onToggleElementsVisibility}
                    />
                  )}
                </ElementGroupDefaultRow>
              );
            })}
        </ElementGroupScrollableDivideList>
      </ul>
    </>
  );
});
